import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { products } from "../data/products";

const CartContext = createContext(null);
const STORAGE_KEY = "hwl_cart_v1";
const MAX_QTY = 50;

const load = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return raw.filter((i) => products.some((p) => p.sku === i.sku) && Number.isInteger(i.quantity) && i.quantity > 0 && i.quantity <= MAX_QTY);
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const setQuantity = useCallback((sku, quantity) => {
    const q = Math.max(0, Math.min(MAX_QTY, Math.floor(Number(quantity) || 0)));
    setItems((prev) => (q === 0 ? prev.filter((i) => i.sku !== sku) : prev.some((i) => i.sku === sku) ? prev.map((i) => (i.sku === sku ? { ...i, quantity: q } : i)) : [...prev, { sku, quantity: q }]));
  }, []);

  const addItem = useCallback((sku, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.sku === sku);
      const q = Math.min(MAX_QTY, (existing?.quantity || 0) + Math.max(1, Math.floor(Number(quantity) || 1)));
      return existing ? prev.map((i) => (i.sku === sku ? { ...i, quantity: q } : i)) : [...prev, { sku, quantity: q }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((sku) => setItems((prev) => prev.filter((i) => i.sku !== sku)), []);
  const clear = useCallback(() => setItems([]), []);

  const lines = useMemo(() => items.map((i) => {
    const p = products.find((x) => x.sku === i.sku);
    const unit = Number(p.price.replace("$", ""));
    return { ...i, name: p.name, model: p.model, unit, unitLabel: p.priceUnit, lineTotal: unit * i.quantity, to: p.to, image: p.image };
  }), [items]);

  const subtotal = lines.reduce((s, l) => s + l.lineTotal, 0);
  const count = lines.reduce((s, l) => s + l.quantity, 0);

  return (
    <CartContext.Provider value={{ items, lines, subtotal, count, addItem, setQuantity, removeItem, clear, isOpen, setIsOpen, MAX_QTY }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
export const fmt = (n) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
